import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Divider, Stack, Tab, Tabs, Typography } from '@mui/material';
import { VisibilityOutlined } from '@mui/icons-material';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../loading-page';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import { ISurvey } from '../../../shared/services/survey/survey.model';
import { SurveyStatusChip } from '../../presentation-components/display-elements-components/survey-status-chip';
import { SurveyPreviewDialog } from '../../dialogs/surveys/survey-preview-dialog';
import { SurveyOverviewTab } from './hub/survey-overview-tab';
import { SurveyQuestionsTab } from './hub/survey-questions-tab';
import { SurveyCalculationsTab } from './hub/survey-calculations-tab';
import { SurveyRecipientsTab } from './hub/survey-recipients-tab';
import { SurveyResponsesTab } from './hub/survey-responses-tab';
import { SurveyResultsTab } from './hub/survey-results-tab';

export type SurveyHubTabId = 'overview' | 'questions' | 'calculations' | 'recipients' | 'responses' | 'results';

export interface SurveyHubTabProps {
  survey: ISurvey;
  /** Reloads the survey; returns the fetch promise so callers can await the fresh data. */
  reload: () => void | Promise<unknown>;
  /** The survey's question set is partner-managed and not editable by this client. */
  isPartnerLocked: boolean;
  /** Navigate to another hub tab (e.g. after activating, go to recipients). */
  goToTab: (tab: SurveyHubTabId) => void;
}

/**
 * One home per survey: everything about building, sending, tracking and using a
 * survey lives in the tabs here instead of scattered sibling pages.
 */
export const SurveyHubPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [survey, setSurvey] = useState<ISurvey>();
  const [isActivating, setIsActivating] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);

  const loadSurvey = useCallback(async () => {
    if (!id) return;
    try {
      setSurvey(await surveyAdminService.getSurvey(id));
    } catch {
      setSurvey(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { loadSurvey(); }, [loadSurvey]);

  const isProductData = survey?.purpose === 'SUPPLIER_PRODUCT_DATA';

  const tabs = useMemo((): { id: SurveyHubTabId; label: string }[] => (
    isProductData
      ? [
        { id: 'overview', label: 'Overview' },
        { id: 'recipients', label: 'Recipients' },
        { id: 'responses', label: 'Responses' },
        { id: 'results', label: 'Results' },
      ]
      : [
        { id: 'overview', label: 'Overview' },
        { id: 'questions', label: 'Questions' },
        { id: 'calculations', label: 'Calculations' },
        { id: 'recipients', label: 'Recipients' },
        { id: 'responses', label: 'Responses' },
        { id: 'results', label: 'Results' },
      ]
  ), [isProductData]);

  const requestedTab = searchParams.get('tabId') as SurveyHubTabId | null;
  const activeTab: SurveyHubTabId = tabs.some(t => t.id === requestedTab) ? requestedTab! : 'overview';

  const goToTab = useCallback((tab: SurveyHubTabId) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tabId', tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setStatus = async (status: 'ACTIVE' | 'ARCHIVED' | 'DRAFT', successMessage: string) => {
    if (!id) return;
    setIsActivating(true);
    try {
      await surveyAdminService.updateSurvey(id, { status });
      userNotificationsContext.openSnackbar({ open: true, message: successMessage, backgroundColor: 'primary.dark' });
      loadSurvey();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to update the survey status', backgroundColor: 'text.primary' });
    } finally {
      setIsActivating(false);
    }
  };

  // Advisory preflight: activating with no questions or nobody to answer is almost
  // certainly a mistake, but nothing server-side forbids it, so offer Activate anyway.
  const activateWithPreflight = async (successMessage: string) => {
    if (!id || !survey) return;
    setIsActivating(true);
    const warnings: string[] = [];
    try {
      if (survey.purpose !== 'SUPPLIER_PRODUCT_DATA' && !(survey.surveyQuestionGroup?.surveyQuestionGroupItems?.length)) {
        warnings.push('It has no questions yet, so respondents will see an empty survey.');
      } else if (survey.purpose !== 'SUPPLIER_PRODUCT_DATA') {
        // A rule only produces emissions when it has a conversion factor attached;
        // intermediate (adjustment) values never produce emissions on their own.
        const rules = survey.surveyQuestionGroup?.surveyQuestionGroupEmissionRules ?? [];
        const producingRules = rules.filter(r => !r.isAdjustment && r.conversionId);
        if (producingRules.length === 0) {
          warnings.push('This survey has no calculations yet, so responses will not produce any emissions.');
        }
      }
      const hasOpenLink = survey.distributionMode === 'OPEN_LINK_ONLY' || survey.distributionMode === 'BOTH';
      if (!hasOpenLink) {
        try {
          const invitations = await surveyAdminService.getInvitations(id, { page: 0, perPage: 1 });
          if (invitations.count === 0) {
            warnings.push('It has no recipients yet. Add recipients from the Recipients tab.');
          }
        } catch {
          // Preflight is advisory; a failed check never blocks activation.
        }
      }
    } finally {
      setIsActivating(false);
    }
    if (warnings.length === 0) {
      await setStatus('ACTIVE', successMessage);
      return;
    }
    userNotificationsContext.openDialog({
      active: true,
      title: 'Activate this survey?',
      message: `Before activating, note: ${warnings.join(' ')}`,
      buttonCopy: 'Activate anyway',
      cancelButtonCopy: 'Not yet',
    }).then(ok => {
      if (ok) setStatus('ACTIVE', successMessage);
    }).catch(() => undefined);
  };

  const isPartnerLocked = survey?.surveyQuestionGroup?.canClientEdit === false;

  return (
    <PageOuterContainer>
      <LoadingPage pageReady={!!survey}>
        <PageHeading
          title={survey?.name ?? ''}
          includeBackButton
          backButtonText='Back to Surveys'
          backButtonLink='/surveys'
          extraActions={!isProductData && survey ? (
            <Button
              variant='outlined'
              startIcon={<VisibilityOutlined fontSize='small' />}
              onClick={() => setOpenPreview(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              <Typography sx={{ pl: 0.5 }}>Preview</Typography>
            </Button>
          ) : undefined}
        />

        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5 }}>
          {survey && <SurveyStatusChip status={survey.status} />}
        </Stack>

        {survey?.status === 'DRAFT' && (
          <Alert
            severity='warning'
            sx={{ mb: 2 }}
            action={(
              <Button color='inherit' size='small' disabled={isActivating} onClick={() => activateWithPreflight('Survey activated. Recipients can now respond.')}>
                Activate
              </Button>
            )}
          >
            This survey is a draft. Recipients cannot respond until it is activated.
          </Alert>
        )}
        {survey?.status === 'ARCHIVED' && (
          <Alert
            severity='info'
            sx={{ mb: 2 }}
            action={(
              <Button color='inherit' size='small' disabled={isActivating} onClick={() => setStatus('ACTIVE', 'Survey reopened. Recipients can respond again.')}>
                Reopen
              </Button>
            )}
          >
            This survey is archived. Recipients cannot respond, and it is hidden from active lists.
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={(_, v) => goToTab(v as SurveyHubTabId)}
          variant='scrollable'
          allowScrollButtonsMobile
          sx={{ mb: 1 }}
        >
          {tabs.map(t => <Tab key={t.id} value={t.id} label={t.label} />)}
        </Tabs>
        <Divider />

        {survey && (
          <Box sx={{ pt: 2 }}>
            {activeTab === 'overview' && (
              <SurveyOverviewTab
                survey={survey}
                reload={loadSurvey}
                isPartnerLocked={isPartnerLocked}
                goToTab={goToTab}
                onArchive={() => setStatus('ARCHIVED', 'Survey archived')}
                onActivate={() => activateWithPreflight('Survey activated. Recipients can now respond.')}
                statusBusy={isActivating}
              />
            )}
            {activeTab === 'questions' && !isProductData && (
              <SurveyQuestionsTab survey={survey} reload={loadSurvey} isPartnerLocked={isPartnerLocked} goToTab={goToTab} />
            )}
            {activeTab === 'calculations' && !isProductData && (
              <SurveyCalculationsTab survey={survey} reload={loadSurvey} isPartnerLocked={isPartnerLocked} goToTab={goToTab} />
            )}
            {activeTab === 'recipients' && (
              <SurveyRecipientsTab survey={survey} reload={loadSurvey} isPartnerLocked={isPartnerLocked} goToTab={goToTab} />
            )}
            {activeTab === 'responses' && (
              <SurveyResponsesTab survey={survey} reload={loadSurvey} isPartnerLocked={isPartnerLocked} goToTab={goToTab} />
            )}
            {activeTab === 'results' && (
              <SurveyResultsTab survey={survey} reload={loadSurvey} isPartnerLocked={isPartnerLocked} goToTab={goToTab} />
            )}
          </Box>
        )}

        {/* Mounted only while open so every preview starts from a clean run;
            reopening can never flash the previous run's state. */}
        {survey && openPreview && (
          <SurveyPreviewDialog
            open={openPreview}
            onClose={() => setOpenPreview(false)}
            survey={survey}
          />
        )}
      </LoadingPage>
    </PageOuterContainer>
  );
};
